import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Icons,
  useTheme,
  useMediaQuery,
} from '@ublend-npm/aulaui-core';
import { getHomeViewRole } from '@app/selectors/home';
import { location as getLocation } from '@app/selectors/routing';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import {
  NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS,
  NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS_VIEW_ALL_MODAL,
} from '@core/constants/analytics';
import { trackHomeViewAllClicked } from '../common';
import { Lti1p3Launch } from '@app/components/presentational/ClassRoom/Assignments/Lti1p3Launch/Lti1p3Launch';
import { getStarredSpaceColors } from '../../colors';
import { WidgetCard, WidgetErrorState } from '../common';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';
import UpcomingAssignmentRowSkeleton from './UpcomingAssignmentRowSkeleton';
import UpcomingAssignmentsModal from './UpcomingAssignmentsModal';
import UpcomingAssignmentsInfoModal from './UpcomingAssignmentsInfoModal';
import UpcomingAssignmentsEmptyState from './UpcomingAssignmentsEmptyState';
import useHomeUpcomingAssignments from './useHomeUpcomingAssignments';
import useOpenUpcomingAssignment from './useOpenUpcomingAssignment';
import { getAssignmentTermLabel } from './getAssignmentTermLabel';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';
import { saveHomeScrollPosition } from '../../homeScrollPosition';

const WIDGET_LIMIT = 3;
const WIDGET_KEY = 'home-upcoming-assignments-widget';

const STRINGS = {
  title: 'Upcoming assignments',
  viewAll: 'View all upcoming assignments',
  hide: 'Hide',
  about: 'About upcoming assignments',
};

const UpcomingAssignmentsWidget = () => {
  const theme = useTheme();
  const { palette } = theme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<any>();
  const viewRole = useSelector(getHomeViewRole);
  const pathname = useSelector(getLocation)?.pathname ?? '';
  const classRooms = useSelector((state: any) => state.data?.classRooms || {});
  const {
    allItems,
    errorType,
    isLoading,
    isLoadingMore,
    hasMore,
    total,
    loadMore,
    retry,
  } = useHomeUpcomingAssignments({ viewRole });
  const { openAssignment, ltiLaunch, clearLtiLaunch, handleLtiLaunchError } =
    useOpenUpcomingAssignment();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const colors = getStarredSpaceColors(palette);

  const titleLeading = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={40}
      height={40}
      mr={1}
      flexShrink={0}
      sx={{
        borderRadius: '100px',
        bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#EAE8ED',
        color: 'primary.main',
      }}
    >
      <Icons.AssignmentOutlined sx={{ fontSize: 24 }} />
    </Box>
  );
  const isViewMoreOpen = pathname === '/home/upcoming-assignments';

  useTrackHomeWidgetLoaded(
    'upcoming_assignments',
    !isLoading && !errorType,
    // Prefer API total; fall back to loaded items if total is missing/stale.
    Math.max(total, allItems.length)
  );

  const handleOpenAssignment = useCallback(
    async (
      assignment: Parameters<typeof openAssignment>[0],
      navigationSource: string,
      itemIndex: number
    ) => {
      if (isViewMoreOpen) {
        dispatch({
          type: '@@router/CALL_HISTORY_METHOD',
          payload: {
            method: 'push',
            args: ['/home'],
          },
        });
      }
      await openAssignment(assignment, navigationSource, itemIndex);
    },
    [dispatch, isViewMoreOpen, openAssignment]
  );

  const handleWidgetAssignmentClick = useCallback(
    (assignment: Parameters<typeof openAssignment>[0], itemIndex: number) =>
      handleOpenAssignment(
        assignment,
        NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS,
        itemIndex
      ),
    [handleOpenAssignment]
  );

  const handleViewAllAssignmentClick = useCallback(
    (assignment: Parameters<typeof openAssignment>[0], itemIndex: number) =>
      handleOpenAssignment(
        assignment,
        NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS_VIEW_ALL_MODAL,
        itemIndex
      ),
    [handleOpenAssignment]
  );

  const getTermLabel = useCallback(
    (spaceId: string) => getAssignmentTermLabel(classRooms[spaceId]),
    [classRooms]
  );

  const widgetItems = useMemo(
    () => allItems.slice(0, WIDGET_LIMIT),
    [allItems]
  );

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference(WIDGET_KEY, true));
  };

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const titleAdornment = (
    <IconButton
      aria-label={STRINGS.about}
      size="small"
      onClick={() => setIsInfoModalOpen(true)}
      sx={{
        width: 24,
        height: 24,
        color: colors.widgetMenuIcon,
        p: 0,
      }}
    >
      <Icons.InfoOutlined sx={{ fontSize: 20 }} />
    </IconButton>
  );

  const footer =
    allItems.length > WIDGET_LIMIT && !errorType ? (
      <Button
        fullWidth
        kind="secondary"
        endIcon={<Icons.ArrowForward />}
        onClick={() => {
          if (isMobile) {
            saveHomeScrollPosition();
          }

          trackHomeViewAllClicked(
            NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS,
            allItems.length
          );
          dispatch({
            type: '@@router/CALL_HISTORY_METHOD',
            payload: {
              method: 'push',
              args: ['/home/upcoming-assignments'],
            },
          });
        }}
        disabled={isLoading}
        sx={{
          width: '100%',
          color: colors.starIcon,
          borderRadius: '4px',
        }}
      >
        {STRINGS.viewAll}
      </Button>
    ) : null;

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <UpcomingAssignmentRowSkeleton />
          <UpcomingAssignmentRowSkeleton />
          <UpcomingAssignmentRowSkeleton showDivider={false} />
        </>
      );
    }
    if (errorType) {
      return (
        <WidgetErrorState
          type={errorType}
          onRetry={retry}
          isRetrying={isLoading}
        />
      );
    }

    if (!widgetItems.length) {
      return <UpcomingAssignmentsEmptyState />;
    }

    return widgetItems.map((assignment, index) => (
      <UpcomingAssignmentRow
        key={assignment.id}
        assignment={assignment}
        viewRole={viewRole}
        termLabel={getTermLabel(assignment.spaceId)}
        showDivider={index < widgetItems.length - 1}
        itemIndex={index}
        onOpen={handleWidgetAssignmentClick}
      />
    ));
  }, [
    errorType,
    getTermLabel,
    handleWidgetAssignmentClick,
    isLoading,
    retry,
    viewRole,
    widgetItems,
  ]);

  return (
    <>
      <WidgetCard
        title={STRINGS.title}
        titleLeading={titleLeading}
        titleAdornment={titleAdornment}
        menuActions={menuActions}
        footer={footer}
        contentGap={0}
        aria-label="Upcoming assignments widget"
      >
        {content}
      </WidgetCard>

      <UpcomingAssignmentsModal
        open={isViewMoreOpen}
        onClose={() =>
          dispatch({
            type: '@@router/CALL_HISTORY_METHOD',
            payload: {
              method: 'push',
              args: ['/home'],
            },
          })
        }
        assignments={allItems}
        viewRole={viewRole}
        isLoading={isLoading}
        getTermLabel={getTermLabel}
        onOpenAssignment={handleViewAllAssignmentClick}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />

      <UpcomingAssignmentsInfoModal
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        viewRole={viewRole}
      />

      {ltiLaunch && (
        <Lti1p3Launch
          providerId={ltiLaunch.providerId}
          resourceLinkId={ltiLaunch.resourceLinkId}
          resourceLinkTitle={ltiLaunch.title}
          assignmentId={ltiLaunch.assignmentId}
          onSuccessLaunch={clearLtiLaunch}
          onErrorLaunch={handleLtiLaunchError}
        />
      )}
    </>
  );
};

export default UpcomingAssignmentsWidget;
